/**
 * Anwesenheit — Anzeige-Regeln ohne React (v1.405.0, getestet in __tests__/attendance.test.ts).
 *
 * Erlaubt ist, was der Server erlaubt (Zustandsautomat in backend/src/services/attendance/
 * attendanceRules.ts); die Knöpfe zeigen nur die Aktionen, die im aktuellen Zustand gehen. Laufende
 * Zeiten rechnet die Oberfläche aus dem letzten Server-Stand weiter — mit dem Abstand zur Serveruhr,
 * nicht mit der Gerätezeit allein.
 */
import type { AttendanceState, MyAttendance, PunchType } from './api/domains/attendance';

export const STATE_LABEL: Record<AttendanceState, string> = {
  WORKING: 'arbeitet',
  ON_BREAK: 'Pause',
  OFF: 'weg',
};

export const STATE_COLOR: Record<AttendanceState, string> = {
  WORKING: 'green',
  ON_BREAK: 'yellow',
  OFF: 'gray',
};

export const PUNCH_LABEL: Record<PunchType, string> = {
  IN: 'Eingestempelt',
  BREAK_START: 'Pause begonnen',
  BREAK_END: 'Pause beendet',
  OUT: 'Ausgestempelt',
};

export const ACTION_LABEL: Record<PunchType, string> = {
  IN: 'Einstempeln',
  BREAK_START: 'Pause beginnen',
  BREAK_END: 'Pause beenden',
  OUT: 'Ausstempeln',
};

export const ACTION_COLOR: Record<PunchType, string> = {
  IN: 'green',
  BREAK_START: 'yellow',
  BREAK_END: 'green',
  OUT: 'red',
};

/** Welche Knöpfe im Zustand angeboten werden — dieselben Übergänge wie auf dem Server. */
export function availableActions(state: AttendanceState): PunchType[] {
  switch (state) {
    case 'OFF':
      return ['IN'];
    case 'WORKING':
      return ['BREAK_START', 'OUT'];
    case 'ON_BREAK':
      return ['BREAK_END', 'OUT'];
  }
}

const berlinTime = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' });
const berlinDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' });
const berlinShortDate = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit' });

/** „08:12" (heute) bzw. „30.10. 08:12" — immer Berliner Uhr. */
export function formatSince(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const time = berlinTime.format(d);
  return berlinDay.format(d) === berlinDay.format(now) ? time : `${berlinShortDate.format(d)} ${time}`;
}

/** „3:07 h" — Stunden und Minuten für die Kopfzeile. */
export function formatMinutes(minutes: number): string {
  const m = Math.max(0, Math.floor(minutes));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')} h`;
}

/**
 * Heutige Zeiten jetzt — Server-Stand plus die seitdem vergangene Zeit im laufenden Zustand.
 * `fetchedAt` ist die Gerätezeit beim Abruf; gemessen wird nur der Abstand seitdem.
 */
export function liveTotals(status: Pick<MyAttendance, 'state' | 'today'>, fetchedAt: number, deviceNow: number): {
  workMinutes: number;
  breakMinutes: number;
} {
  const elapsed = Math.max(0, (deviceNow - fetchedAt) / 60_000);
  return {
    workMinutes: status.today.workMinutes + (status.state === 'WORKING' ? elapsed : 0),
    breakMinutes: status.today.breakMinutes + (status.state === 'ON_BREAK' ? elapsed : 0),
  };
}

/** Status-Satz für Kopfzeile und Startseite, z. B. „Arbeitet seit 08:12". */
export function statusSentence(state: AttendanceState, since: string | null, now: Date = new Date()): string {
  const at = formatSince(since, now);
  if (state === 'WORKING') return at ? `Arbeitet seit ${at}` : 'Arbeitet';
  if (state === 'ON_BREAK') return at ? `Pause seit ${at}` : 'Pause';
  return at ? `Nicht eingestempelt (zuletzt ${at})` : 'Nicht eingestempelt';
}

/** Eingestempelt seit einem früheren Tag — vermutlich Ausstempeln vergessen. */
export function isStaleShift(state: AttendanceState, since: string | null, now: Date = new Date()): boolean {
  if (state === 'OFF' || !since) return false;
  return berlinDay.format(new Date(since)) !== berlinDay.format(now);
}

export type ClockInHintContext = 'booking' | 'report';

/**
 * Hinweis „nicht eingestempelt" beim Zeit buchen bzw. Bericht anlegen — nur ein Hinweis, keine
 * Sperre (Vorgabe Kevin). Pause zählt als eingestempelt. Solange der Status unbekannt ist (lädt,
 * Fehler), wird nichts angezeigt — lieber kein Hinweis als ein falscher.
 */
export function clockInHint(state: AttendanceState | null | undefined, context: ClockInHintContext): string | null {
  if (state !== 'OFF') return null;
  return context === 'booking'
    ? 'Du buchst Zeit, bist aber nicht eingestempelt.'
    : 'Du legst einen Servicebericht an, bist aber nicht eingestempelt.';
}

/** Eine Referenz je gemeinter Aktion (Doppeltipp, Neuversuch → derselbe Stempel). */
export function newPunchRef(): string {
  return `tap-${crypto.randomUUID()}`;
}
